import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  VirtualizedFormSelect,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@roger/ui-components';
import { Button } from '@roger/ui-components';
import {
  Loader2,
  CheckCircle,
  XCircle,
  Zap,
  ChevronRight,
  AlertCircle,
  Check,
  HelpCircle,
} from 'lucide-react';
import { useToast } from '../../hooks/useToastContext';
import {
  checkEligibility,
  getEligibilityChecks,
  client,
} from '../../services/api';
import type { IntakeRecord } from '../../store/slices/intakeSlice';
import type { EligibilityCheckResponse } from '../../services/api';
import EligibilityDetailsModal from '../EligibilityDetailsModal';
import { formatDateTimeInPT } from '../../utils/dateUtils';
import { Field, DateField } from '../FormFields';
import { useIntake } from '../../hooks/useIntake';
import MarkCompletedButton from './MarkCompletedButton';
import {
  formatEligibilityErrorForDisplay,
  GENERIC_ERROR_MESSAGE,
  type StediAAAError,
} from '../../utils/eligibilityErrorMessages';
import { AiExtractedInsuranceSuggestion } from './AiExtractedInsuranceSuggestion';

interface EligibilityCheckStepProps {
  selectedIntake: IntakeRecord | null;
  onRefresh?: () => Promise<void>;
  agencyInsuranceOptions: Array<{ value: string; label: string }>;
  agencyInsurancesLoading: boolean;
  agencyInsurancesError: string | null;
  onStatusChange?: () => void | Promise<void>;
}

const EligibilityCheckStep: React.FC<EligibilityCheckStepProps> = ({
  selectedIntake,
  onRefresh,
  agencyInsuranceOptions,
  agencyInsurancesLoading,
  agencyInsurancesError,
  onStatusChange,
}) => {
  const [isRunningCheck, setIsRunningCheck] = useState(false);
  const [pastEligibilityResults, setPastEligibilityResults] = useState<
    EligibilityCheckResponse[]
  >([]);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [selectedEligibilityResult, setSelectedEligibilityResult] =
    useState<EligibilityCheckResponse | null>(null);
  const [isEligibilityModalOpen, setIsEligibilityModalOpen] = useState(false);
  const { success, error } = useToast();
  const { updateIntakeRecord } = useIntake();

  // Editable form state
  const [formData, setFormData] = useState({
    agency_insurance_id: selectedIntake?.agency_insurance_id || '',
    primary_member_id: selectedIntake?.primary_member_id || '',
    patient_first_name: selectedIntake?.patient_first_name || '',
    patient_last_name: selectedIntake?.patient_last_name || '',
    patient_dob: selectedIntake?.patient_dob || '',
    patient_street_address: selectedIntake?.patient_street_address || '',
    patient_city: selectedIntake?.patient_city || '',
    patient_state: selectedIntake?.patient_state || '',
    patient_zip_code: selectedIntake?.patient_zip_code || '',
  });

  // Save status tracking
  const [saveStatus, setSaveStatus] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const debounceTimeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});

  // Ref for insurance select to manually set value
  const insuranceSelectRef = useRef<HTMLInputElement>(null);

  // Update form data when selectedIntake changes
  useEffect(() => {
    if (selectedIntake) {
      setFormData({
        agency_insurance_id: selectedIntake.agency_insurance_id || '',
        primary_member_id: selectedIntake.primary_member_id || '',
        patient_first_name: selectedIntake.patient_first_name || '',
        patient_last_name: selectedIntake.patient_last_name || '',
        patient_dob: selectedIntake.patient_dob || '',
        patient_street_address: selectedIntake.patient_street_address || '',
        patient_city: selectedIntake.patient_city || '',
        patient_state: selectedIntake.patient_state || '',
        patient_zip_code: selectedIntake.patient_zip_code || '',
      });
    }
  }, [selectedIntake]);

  // Set insurance select value when formData changes or options load
  useEffect(() => {
    if (insuranceSelectRef.current && formData.agency_insurance_id) {
      insuranceSelectRef.current.value = formData.agency_insurance_id;
      // Trigger change event to ensure VirtualizedFormSelect syncs
      insuranceSelectRef.current.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    }
  }, [formData.agency_insurance_id, agencyInsuranceOptions]);

  // Cleanup debounce timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimeoutsRef.current).forEach(clearTimeout);
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  // Auto-save handler
  const handleAutoSave = useCallback(
    async (fieldName: string, value: unknown) => {
      if (!selectedIntake) return;

      try {
        setSaveStatus('saving');

        await updateIntakeRecord(selectedIntake.id, { [fieldName]: value });

        // Refresh intake data to get updated relationships
        if (onRefresh) {
          await onRefresh();
        }

        setSaveStatus('saved');

        // Clear "saved" status after 2 seconds
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
      } catch (err) {
        console.error('Failed to save field:', err);
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    },
    [selectedIntake, updateIntakeRecord, onRefresh]
  );

  // Field change handler with debounced auto-save for text fields
  const handleFieldChange = useCallback(
    (fieldName: keyof typeof formData, value: string, immediate = false) => {
      setFormData(prev => ({ ...prev, [fieldName]: value }));

      // Clear any existing debounce for this field
      if (debounceTimeoutsRef.current[fieldName]) {
        clearTimeout(debounceTimeoutsRef.current[fieldName]);
      }

      if (immediate) {
        // Save immediately for dropdown/date changes
        handleAutoSave(fieldName, value);
      } else {
        // Debounce text field saves (1.5 seconds)
        debounceTimeoutsRef.current[fieldName] = setTimeout(() => {
          handleAutoSave(fieldName, value);
          delete debounceTimeoutsRef.current[fieldName];
        }, 1500);
      }
    },
    [handleAutoSave]
  );

  // Field blur handler for immediate save
  const handleFieldBlur = useCallback(
    (fieldName: keyof typeof formData) => {
      // Cancel any pending debounce
      if (debounceTimeoutsRef.current[fieldName]) {
        clearTimeout(debounceTimeoutsRef.current[fieldName]);
        delete debounceTimeoutsRef.current[fieldName];
      }

      // Save if value has changed
      const currentValue = formData[fieldName];
      const originalValue = selectedIntake?.[fieldName] || '';
      if (currentValue !== originalValue) {
        handleAutoSave(fieldName, currentValue);
      }
    },
    [formData, selectedIntake, handleAutoSave]
  );

  // Check if a completed request should be treated as completed with error due to errors
  const getEffectiveStatus = (
    result: EligibilityCheckResponse
  ): EligibilityCheckResponse['status'] | 'completed_with_error' => {
    if (
      result.status === 'completed' &&
      (result.raw_response?.error ||
        ((result.raw_response?.errors as Array<Record<string, unknown>>) || [])
          .length > 0)
    ) {
      return 'completed_with_error';
    }
    return result.status;
  };

  // Format status text for display
  const getStatusText = (
    status: EligibilityCheckResponse['status'] | 'completed_with_error'
  ): string => {
    const statusMap: Record<string, string> = {
      pending: 'Pending',
      completed: 'Completed',
      failed: 'Failed',
      expired: 'Expired',
      in_progress: 'In progress',
      cancelled: 'Cancelled',
      completed_with_error: 'Completed with error',
    };

    return statusMap[status] || String(status);
  };

  const handleViewDetails = (result: EligibilityCheckResponse) => {
    setSelectedEligibilityResult(result);
    setIsEligibilityModalOpen(true);
  };

  // Fetch past eligibility check results
  const fetchPastEligibilityResults = async () => {
    if (!selectedIntake?.id) return;

    setIsLoadingResults(true);
    try {
      const response = await getEligibilityChecks({
        query: { intake_id: selectedIntake.id },
        client,
      });

      if (response.data) {
        // Sort by request date descending (most recent first)
        const sortedResults = response.data.sort(
          (a, b) =>
            new Date(b.request_date).getTime() -
            new Date(a.request_date).getTime()
        );
        setPastEligibilityResults(sortedResults);
      }
    } catch (err) {
      console.error('Error fetching past eligibility results:', err);
    } finally {
      setIsLoadingResults(false);
    }
  };

  // Load past results when intake changes
  useEffect(() => {
    fetchPastEligibilityResults();
  }, [selectedIntake?.id]);

  const handleRunEligibilityCheck = async () => {
    if (!selectedIntake) return;

    setIsRunningCheck(true);
    try {
      const result = await checkEligibility({
        path: { intake_id: selectedIntake.id },
        client,
      });

      // Check if the API call itself failed (non-200 response)
      if (result.error) {
        // Try to extract AAA errors from the error response
        const errorData = result.error as Record<string, unknown>;
        const aaaErrors = (errorData?.stedi_errors || errorData?.errors) as
          | StediAAAError[]
          | undefined;
        const formattedError = formatEligibilityErrorForDisplay(
          null,
          aaaErrors
        );
        const errorMessage =
          formattedError?.message || GENERIC_ERROR_MESSAGE.message;
        const suggestion =
          formattedError?.suggestion || GENERIC_ERROR_MESSAGE.suggestion;
        error('Eligibility Check Failed', `${errorMessage}\n\n${suggestion}`);
      }
      // Check if the result has an error message or errors (failed eligibility check)
      else if (
        result.data?.error_message ||
        (result.data?.raw_response?.errors as StediAAAError[] | undefined)
          ?.length
      ) {
        const formattedError = formatEligibilityErrorForDisplay(
          result.data.error_message,
          result.data.raw_response?.errors as StediAAAError[] | undefined
        );
        const errorMessage =
          formattedError?.message || GENERIC_ERROR_MESSAGE.message;
        const suggestion =
          formattedError?.suggestion || GENERIC_ERROR_MESSAGE.suggestion;
        error('Eligibility Check Failed', `${errorMessage}\n\n${suggestion}`);
      } else {
        success(
          'Eligibility Check Complete',
          'Eligibility check completed successfully.'
        );
      }

      // Refresh the intake data and eligibility results
      if (onRefresh) {
        await onRefresh();
      }
      await fetchPastEligibilityResults();
    } catch (err: unknown) {
      console.error('Error running eligibility check:', err);
      // Show generic error message for unexpected errors
      error(
        'Eligibility Check Failed',
        `${GENERIC_ERROR_MESSAGE.message}\n\n${GENERIC_ERROR_MESSAGE.suggestion}`
      );
    } finally {
      setIsRunningCheck(false);
    }
  };

  // Determine if we can run eligibility check
  const canRunEligibilityCheck =
    selectedIntake &&
    selectedIntake.agency_clinic_id &&
    selectedIntake.agency_insurance?.edi_payer_id &&
    selectedIntake.agency_insurance?.stedi_eligibility_enabled &&
    formData.agency_insurance_id &&
    formData.primary_member_id &&
    formData.primary_member_id.trim() !== '';

  return (
    <Card className='h-full flex flex-col'>
      <CardHeader className='border-b py-3 px-4'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            {/* Save Status Indicator */}
            {saveStatus === 'saving' && (
              <div className='flex items-center gap-2 text-xs text-gray-500'>
                <Loader2 className='h-3 w-3 animate-spin' />
                <span>Saving...</span>
              </div>
            )}
            {saveStatus === 'saved' && (
              <div className='flex items-center gap-2 text-xs text-green-600'>
                <Check className='h-3 w-3' />
                <span>Saved</span>
              </div>
            )}
            {saveStatus === 'error' && (
              <div className='flex items-center gap-2 text-xs text-red-600'>
                <AlertCircle className='h-3 w-3' />
                <span>Save failed</span>
              </div>
            )}
          </div>
          {selectedIntake && (
            <MarkCompletedButton
              intakeId={selectedIntake.id}
              tabKey='eligibility'
              isComplete={
                selectedIntake.tab_statuses?.eligibility === 'complete' ||
                selectedIntake.tab_statuses?.eligibility ===
                  'complete_with_errors'
              }
              onStatusChange={onStatusChange}
              className='ml-2'
            />
          )}
        </div>
      </CardHeader>
      <CardContent className='flex-1 overflow-y-auto p-4 pt-3'>
        <div className='space-y-6'>
          {/* Current Status */}
          <div className='space-y-4'>
            {/* Two Column Layout for Insurance and Patient */}
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
              {/* Insurance Information */}
              <div className='border px-4 py-3 space-y-4'>
                <div className='font-semibold mb-2'>Insurance Details</div>

                {/* Mapped Insurance Selection */}
                <div>
                  {!selectedIntake?.agency_clinic_id ? (
                    <div className='text-sm text-orange-700 bg-orange-50 p-2 rounded'>
                      Please select a clinic first to see available insurances
                    </div>
                  ) : (
                    <div>
                      <div className='flex items-center gap-2 mb-2'>
                        <label className='text-sm font-medium text-gray-700'>
                          Primary Insurance
                        </label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button className='text-gray-400 hover:text-gray-600 transition-colors'>
                              <HelpCircle className='h-4 w-4' />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className='w-72' align='start'>
                            <div className='space-y-2 text-sm'>
                              <p>
                                These are insurances configured for your clinic
                                in Kinnser.
                              </p>
                              <p className='text-gray-600'>
                                <span>🟢</span> indicates the insurance supports
                                eligibility checks.
                              </p>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <VirtualizedFormSelect
                        ref={insuranceSelectRef}
                        label=''
                        placeholder={
                          agencyInsurancesLoading
                            ? 'Loading insurances...'
                            : agencyInsurancesError
                              ? 'Error loading insurances'
                              : 'Select insurance from your EMR'
                        }
                        options={agencyInsuranceOptions}
                        required={true}
                        disabled={agencyInsurancesLoading}
                        error={
                          formData.agency_insurance_id &&
                          !selectedIntake?.agency_insurance?.edi_payer_id
                            ? 'Cannot run eligibility check, selected insurance has not been configured with a payer ID'
                            : formData.agency_insurance_id &&
                                selectedIntake?.agency_insurance
                                  ?.edi_payer_id &&
                                !selectedIntake?.agency_insurance
                                  ?.stedi_eligibility_enabled
                              ? 'Eligibility checks are not enabled for this insurance'
                              : undefined
                        }
                        name='agency_insurance_id'
                        onChange={e => {
                          handleFieldChange(
                            'agency_insurance_id',
                            e.target.value,
                            true
                          );
                        }}
                      />
                      <div className='text-xs text-gray-500 mt-1'>
                        ✓ = supports eligibility checks
                      </div>
                      {selectedIntake?.primary_insurance && (
                        <AiExtractedInsuranceSuggestion
                          value={selectedIntake?.primary_insurance}
                        />
                      )}
                    </div>
                  )}
                </div>

                {/* Member ID */}
                <div>
                  <Field
                    label='Member ID / Policy Number'
                    value={formData.primary_member_id}
                    htmlFor='primary_member_id'
                    required={true}
                    warning={!formData.primary_member_id}
                    error={
                      !formData.primary_member_id
                        ? 'Member ID is required for eligibility check'
                        : undefined
                    }
                    onChange={value =>
                      handleFieldChange('primary_member_id', value)
                    }
                    onBlur={() => handleFieldBlur('primary_member_id')}
                  />
                </div>
              </div>

              {/* Patient Information - Editable */}
              <div className='border px-4 py-3 space-y-3'>
                <div className='font-semibold mb-2'>Patient Information</div>

                {/* Patient Name */}
                <div className='grid grid-cols-2 gap-3'>
                  <Field
                    label='First Name'
                    value={formData.patient_first_name}
                    htmlFor='patient_first_name'
                    required={true}
                    onChange={value =>
                      handleFieldChange('patient_first_name', value)
                    }
                    onBlur={() => handleFieldBlur('patient_first_name')}
                  />
                  <Field
                    label='Last Name'
                    value={formData.patient_last_name}
                    htmlFor='patient_last_name'
                    required={true}
                    onChange={value =>
                      handleFieldChange('patient_last_name', value)
                    }
                    onBlur={() => handleFieldBlur('patient_last_name')}
                  />
                </div>

                {/* Date of Birth */}
                <DateField
                  label='Date of Birth'
                  value={
                    typeof formData.patient_dob === 'string'
                      ? formData.patient_dob
                      : undefined
                  }
                  htmlFor='patient_dob'
                  required={true}
                  yearRange={{
                    fromYear: 1900,
                    toYear: new Date().getFullYear(),
                  }}
                  onChange={value => {
                    handleFieldChange('patient_dob', value || '', true);
                  }}
                />

                {/* Address */}
                <Field
                  label='Street Address'
                  value={formData.patient_street_address}
                  htmlFor='patient_street_address'
                  onChange={value =>
                    handleFieldChange('patient_street_address', value)
                  }
                  onBlur={() => handleFieldBlur('patient_street_address')}
                />
                <div className='grid grid-cols-3 gap-3'>
                  <Field
                    label='City'
                    value={formData.patient_city}
                    htmlFor='patient_city'
                    onChange={value => handleFieldChange('patient_city', value)}
                    onBlur={() => handleFieldBlur('patient_city')}
                  />
                  <Field
                    label='State'
                    value={formData.patient_state}
                    htmlFor='patient_state'
                    onChange={value =>
                      handleFieldChange('patient_state', value)
                    }
                    onBlur={() => handleFieldBlur('patient_state')}
                  />
                  <Field
                    label='Zip Code'
                    value={formData.patient_zip_code}
                    htmlFor='patient_zip_code'
                    onChange={value =>
                      handleFieldChange('patient_zip_code', value)
                    }
                    onBlur={() => handleFieldBlur('patient_zip_code')}
                  />
                </div>
              </div>
            </div>

            {/* Insurance Verification Status */}
            <div className='border px-4 py-3 flex items-center gap-3'>
              {!selectedIntake?.insurance_verified ? (
                <XCircle className='h-4 w-4 flex-shrink-0' />
              ) : (
                <CheckCircle className='h-4 w-4 flex-shrink-0' />
              )}
              <div className='flex-1'>
                <div className='font-medium text-sm'>
                  Insurance Verification:{' '}
                  {selectedIntake?.insurance_verified
                    ? 'Verified'
                    : 'Not Verified'}
                </div>
              </div>
            </div>
          </div>

          {/* Run Eligibility Check */}
          <div className='space-y-4 pt-2'>
            <Button
              onClick={handleRunEligibilityCheck}
              disabled={isRunningCheck || !canRunEligibilityCheck}
              className='w-full'
              size='lg'
            >
              {isRunningCheck ? (
                <>
                  <Loader2 className='h-3 w-3 animate-spin mr-2' />
                  Checking Eligibility...
                </>
              ) : (
                <>
                  <Zap className='h-3 w-3 mr-2' />
                  Run Eligibility Check
                </>
              )}
            </Button>
            {!canRunEligibilityCheck && selectedIntake && (
              <div className='text-xs text-orange-700 text-center'>
                {!selectedIntake.agency_clinic_id && (
                  <span>Select a clinic • </span>
                )}
                {!formData.agency_insurance_id && (
                  <span>Map to an insurance • </span>
                )}
                {(!formData.primary_member_id ||
                  formData.primary_member_id.trim() === '') && (
                  <span>Enter member ID</span>
                )}
              </div>
            )}
          </div>

          {/* Past Eligibility Check Results */}
          {selectedIntake?.id && (
            <div className='space-y-4 pt-4 border-t'>
              <div className='font-semibold'>
                Past Eligibility Check Results
              </div>

              {isLoadingResults ? (
                <div className='flex items-center gap-2 text-gray-500'>
                  <Loader2 className='h-3 w-3 animate-spin' />
                  Loading past results...
                </div>
              ) : pastEligibilityResults.length === 0 ? (
                <div className='text-gray-400 italic text-sm'>
                  No previous eligibility checks found for this patient.
                </div>
              ) : (
                <div className='space-y-3'>
                  {pastEligibilityResults.map(result => {
                    const effectiveStatus = getEffectiveStatus(result);
                    return (
                      <div
                        key={result.id}
                        className='border rounded-lg p-3 bg-gray-50 hover:bg-gray-100 hover:border-gray-300 transition-all cursor-pointer group'
                        onClick={() => handleViewDetails(result)}
                        role='button'
                        tabIndex={0}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleViewDetails(result);
                          }
                        }}
                      >
                        <div className='flex items-start gap-3'>
                          {/* Status Icon */}
                          {effectiveStatus === 'completed' ? (
                            <CheckCircle className='h-4 w-4 text-green-600 mt-0.5 flex-shrink-0' />
                          ) : effectiveStatus === 'completed_with_error' ? (
                            <XCircle className='h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0' />
                          ) : effectiveStatus === 'failed' ? (
                            <XCircle className='h-4 w-4 text-red-600 mt-0.5 flex-shrink-0' />
                          ) : effectiveStatus === 'pending' ||
                            effectiveStatus === 'in_progress' ? (
                            <Loader2 className='h-4 w-4 text-yellow-600 animate-spin mt-0.5 flex-shrink-0' />
                          ) : (
                            <XCircle className='h-4 w-4 text-gray-600 mt-0.5 flex-shrink-0' />
                          )}

                          <div className='flex-1 min-w-0'>
                            {/* Header */}
                            <div className='flex items-center justify-between mb-2'>
                              <div className='font-medium text-sm'>
                                <span className='font-medium'>Status:</span>{' '}
                                <span className='text-gray-600'>
                                  {getStatusText(effectiveStatus)}
                                </span>
                              </div>
                              <div className='flex items-center gap-2'>
                                <div className='text-xs text-gray-500'>
                                  {formatDateTimeInPT(result.request_date)}
                                </div>
                                <ChevronRight className='h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors' />
                              </div>
                            </div>

                            {/* Error Message - User-friendly display */}
                            {(() => {
                              const formattedError =
                                formatEligibilityErrorForDisplay(
                                  result.error_message,
                                  result.raw_response?.errors as
                                    | StediAAAError[]
                                    | undefined
                                );
                              if (!formattedError) return null;
                              return (
                                <div className='text-sm mb-2 space-y-1'>
                                  <div>
                                    <span className='font-medium'>Error:</span>{' '}
                                    <span className='text-gray-700'>
                                      {formattedError.message}
                                    </span>
                                  </div>
                                  {formattedError.suggestion && (
                                    <div>
                                      <span className='font-medium'>
                                        Suggestion:
                                      </span>{' '}
                                      <span className='text-gray-600'>
                                        {formattedError.suggestion}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Effective/Termination Dates */}
                            {result.effective_date && (
                              <div className='text-sm mb-2'>
                                <span className='font-medium'>Effective:</span>{' '}
                                <span className='text-gray-700'>
                                  {new Date(
                                    result.effective_date
                                  ).toLocaleDateString()}
                                  {result.termination_date && (
                                    <>
                                      {' '}
                                      -{' '}
                                      {new Date(
                                        result.termination_date
                                      ).toLocaleDateString()}
                                    </>
                                  )}
                                </span>
                              </div>
                            )}

                            {/* Deductible Info */}
                            {result.deductible_info && (
                              <div className='text-sm'>
                                <span className='font-medium'>Deductible:</span>{' '}
                                <span className='text-gray-700'>
                                  {result.deductible_info}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>

      {/* Eligibility Details Modal */}
      <EligibilityDetailsModal
        isOpen={isEligibilityModalOpen}
        onClose={() => setIsEligibilityModalOpen(false)}
        eligibilityResult={selectedEligibilityResult}
      />
    </Card>
  );
};

export default EligibilityCheckStep;
